import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash, Key } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyNew = () => ({ email: "", password: "", name: "" });

export default function UsersPage() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyNew());
  const [pwDialog, setPwDialog] = useState(null); // user obj or null
  const [newPw, setNewPw] = useState("");

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const create = useMutation({
    mutationFn: async (p) => (await api.post("/users", p)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setOpen(false);
      setForm(emptyNew());
      toast.success("Yeni görüntüleyici eklendi");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || "Hata"),
  });

  const resetPw = useMutation({
    mutationFn: async ({ id, password }) =>
      (await api.put(`/users/${id}/password`, { password })).data,
    onSuccess: () => {
      setPwDialog(null);
      setNewPw("");
      toast.success("Şifre güncellendi");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || "Hata"),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Kullanıcı silindi");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || "Hata"),
  });

  const submit = (e) => {
    e.preventDefault();
    create.mutate(form);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">Yönetim</div>
          <h1 className="font-black tracking-tight text-4xl sm:text-5xl">Kullanıcılar</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Yönetici dışındaki kullanıcılar yalnızca görüntüleme yapabilir, hiçbir değişiklik gerçekleştiremez.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="user-new-btn" onClick={() => setForm(emptyNew())}>
              <Plus size={14} className="mr-1" /> Yeni Görüntüleyici
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Görüntüleyici Kullanıcı</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>E-posta</Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="user-email"
                />
              </div>
              <div>
                <Label>Ad (opsiyonel)</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="user-name"
                />
              </div>
              <div>
                <Label>Şifre (en az 6 karakter)</Label>
                <Input
                  required
                  type="password"
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="user-password"
                />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="user-save">
                  Kaydet
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 overflow-x-auto rounded-md border border-border bg-white">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="border-b border-border bg-secondary/50">
            <tr className="text-left text-xs uppercase text-muted-foreground tracking-wider">
              <th className="px-4 py-2">E-posta</th>
              <th className="px-4 py-2">Ad</th>
              <th className="px-4 py-2">Rol</th>
              <th className="px-4 py-2 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                  Yükleniyor...
                </td>
              </tr>
            )}
            {!isLoading && users.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                  Kullanıcı yok.
                </td>
              </tr>
            )}
            {users.map((u) => {
              const isSelf = u.id === me?.id;
              const isAdmin = u.role === "admin";
              return (
                <tr
                  key={u.id}
                  className="border-b border-border/60 hover:bg-secondary/30"
                  data-testid={`user-row-${u.id}`}
                >
                  <td className="px-4 py-3 font-medium">{u.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.name || "-"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase ${
                        isAdmin
                          ? "bg-primary/10 text-primary"
                          : "bg-secondary text-foreground"
                      }`}
                    >
                      {isAdmin ? "Yönetici" : "Görüntüleyici"}
                    </span>
                    {isSelf && (
                      <span className="ml-2 text-[10px] uppercase text-muted-foreground">
                        (siz)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {!isAdmin && (
                        <>
                          <button
                            onClick={() => {
                              setPwDialog(u);
                              setNewPw("");
                            }}
                            data-testid={`user-pw-${u.id}`}
                            className="rounded border border-border p-1 hover:bg-secondary"
                            title="Şifre sıfırla"
                          >
                            <Key size={12} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`${u.email} silinsin mi?`)) remove.mutate(u.id);
                            }}
                            data-testid={`user-del-${u.id}`}
                            className="rounded border border-border p-1 text-destructive hover:bg-destructive/5"
                            title="Sil"
                          >
                            <Trash size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!pwDialog} onOpenChange={(o) => !o && setPwDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Şifre Sıfırla · {pwDialog?.email}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              resetPw.mutate({ id: pwDialog.id, password: newPw });
            }}
            className="space-y-3"
          >
            <div>
              <Label>Yeni Şifre (en az 6 karakter)</Label>
              <Input
                required
                type="password"
                minLength={6}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                data-testid="user-newpw"
              />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="user-pw-save">
                Güncelle
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
